/**
 * useStatistics Hook
 *
 * Custom React hook for getting Canadian financial statistics help text
 * in form components. Uses the pre-loaded AgeStatistics from SettingsContext.
 */

import { useSettings } from "../contexts/SettingsContext";
import { formatStatisticHelpText } from "../types/statistics";

/**
 * Hook to get statistic help text for a specific field.
 * All financial fields MUST have a corresponding stat entry.
 */
export function useFieldStatistic(
  fieldName: string,
  baseHelpText?: string
): string {
  const { statisticsMap, statisticsInitialized } = useSettings();

  if (!statisticsInitialized) {
    return baseHelpText || "";
  }

  const stat = statisticsMap[fieldName];
  if (!stat) {
    throw new Error(
      `Missing statistic for field "${fieldName}". Every financial field must have a stat entry.`
    );
  }

  const statisticText = formatStatisticHelpText(stat);

  if (!baseHelpText) {
    return statisticText;
  }

  return `${baseHelpText}. ${statisticText}`;
}

/**
 * Hook to get multiple field statistics at once.
 * All requested fields MUST have corresponding stat entries.
 */
export function useMultipleFieldStatistics(
  fieldNames: string[]
): Record<string, string> {
  const { statisticsMap, statisticsInitialized } = useSettings();

  const stats: Record<string, string> = {};
  if (!statisticsInitialized) return stats;

  fieldNames.forEach((fieldName) => {
    const stat = statisticsMap[fieldName];
    if (!stat) {
      throw new Error(
        `Missing statistic for field "${fieldName}". Every financial field must have a stat entry.`
      );
    }

    stats[fieldName] = formatStatisticHelpText(stat);
  });
  return stats;
}
